import camelcaseKeys from 'camelcase-keys';

import { baseApi } from 'api/api-base';
import { API_ROUTES } from 'constants/api-routes';
import { setMinerCalculations } from 'store/slices/miners-slice';
import { GetMinerCalculations, GetMinerData, MinerData, UpdateMinerData } from 'types/api/api-miners.types';

export const DEFAULT_MINER_PAGE = 1;
export const DEFAULT_MINER_PAGE_SIZE = 50;

export const minerApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getMinersData: builder.query<GetMinerData.ResponseType, GetMinerData.RequestType>({
      // Paged. The startup call (App.tsx) asks for page 1 to seed the header/dashboard counts;
      // the device pages ask for the page they are showing.
      query: args => ({
        url: API_ROUTES.GET_MINERS_DATA,
        params: {
          page: args?.page ?? DEFAULT_MINER_PAGE,
          pageSize: args?.pageSize ?? DEFAULT_MINER_PAGE_SIZE,
        },
      }),
      extraOptions: {},
      transformResponse: (response: GetMinerData.RawApiResponse | MinerData): GetMinerData.ResponseType => {
        if ('success' in response && response.success && 'data' in response) {
          return response.data;
        }
        return response as MinerData;
      },
    }),

    updateMinerData: builder.mutation<UpdateMinerData.RawApiResponse, UpdateMinerData.RequestType>({
      query: body => ({
        url: API_ROUTES.UPDATE_MINER_DATA,
        method: 'POST',
        body,
      }),
      transformResponse: (response: UpdateMinerData.RawApiResponse): UpdateMinerData.RawApiResponse =>
        camelcaseKeys(response, {
          deep: true,
        }),
      extraOptions: {},
    }),

    getMinerCalculationsData: builder.query<GetMinerCalculations.ResponseType, GetMinerCalculations.RequestType>({
      query: ({ minerId }) => ({
        url: `${API_ROUTES.GET_MINERS_DATA}/${minerId}/${API_ROUTES.CALCULATIONS}`,
      }),
      extraOptions: {},
      transformResponse: (response: GetMinerCalculations.RawApiResponse) =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as GetMinerCalculations.ResponseType,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMinerCalculations({ minerId: arg.minerId, calculations: data.calculations }));
        } catch (error) {
          console.error('Failed to fetch miner calculations', error);
        }
      },
    }),
  }),
});

export const { useGetMinersDataQuery, useUpdateMinerDataMutation, useGetMinerCalculationsDataQuery } = minerApi;
